import { useRef, useState } from 'react';
import { QuantityInput } from '../Form/QuantityInput';
import { Link } from 'react-router-dom';
import { Img } from '../../components/Img/Img';
import { useCart } from '../../state/cart';
import { useTranslation, useFormatting } from '../../i18n/i18n';
import { ApiPreviouslyBoughtProduct } from '../../types';
import './PreviouslyBoughtList.css';
import { Icon, replacement_arrow } from '../Icon/Icon';
import { Button, ButtonLink } from '../Form/Button';
import { FlyToCartContainer } from '../FlyToCart/FlyToCart';
import { PriceTagSimple } from '../../components/PriceTagSimple/PriceTagSimple';
import { ProductContainer } from '../../state/product';

const PreviouslyBoughtItem = ({ product }: { product: ApiPreviouslyBoughtProduct }): JSX.Element => {
	const { dateTimeFormat } = useFormatting();
	const { setQuantity, currentCount } = useCart();
	const { flyToCart } = FlyToCartContainer.useContainer();
	const [quantityField, setQuantityField] = useState(product.product.bundleSize);
	const t = useTranslation().previouslyBoughtPage;
	const urls = useTranslation().routes;
	const imgRef = useRef<HTMLImageElement>(null);
	const handleAddToCart = (id: number) => {
		setQuantity(id, currentCount(id) + quantityField);
		if (imgRef.current) flyToCart();
	};
	const { getFinalPrice } = ProductContainer.useContainer();

	const disabled = product.product.deliveryClass === 6;
	const inStock =
		product.replacement === false ? product.product.quantityInStock : product.replacement.quantityInStock;
	const isStocked = product.replacement === false ? product.product.stocked : product.replacement.stocked;
	const buyButton =
		inStock > 0 && isStocked === true && !disabled ? (
			<Button
				onClick={() => handleAddToCart(product.replacement === false ? product.id : product.replacement.id)}
			>
				{t.buyNow}
			</Button>
		) : disabled ? (
			<Button disabled={disabled} className="disabled">
				{t.buyNow}
			</Button>
		) : (
			<Button
				onClick={() => handleAddToCart(product.replacement === false ? product.id : product.replacement.id)}
				className="caution"
			>
				{t.order}
			</Button>
		);

	const deleted = product.deleted === true;
	const productImg = (
		<Img
			ref={product.replacement ? undefined : imgRef}
			src={product.product.image}
			alt={product.name}
			width={50}
			height={50}
		/>
	);

	return (
		<>
			<div className="previously-bought-products-row">
				<div className="image">
					{deleted ? productImg : <Link to={'/' + product.product.perma}>{productImg}</Link>}
				</div>
				<div className="previously-bought-products-details-wrapper">
					<div className="product">
						{deleted ? (
							<span className="deleted-product">{product.name}</span>
						) : (
							<Link to={'/' + product.product.perma}>{product.name}</Link>
						)}
					</div>
					<div className={`item-no not-mobile ${!product.replacement ? 'deleted-product' : ''}`}>
						<span>
							<span className="grey-label">{t.itemNo}:</span>
							<br />
							<span className={deleted && !product.replacement ? 'deleted-product' : ''}>
								{product.displayId}
							</span>
						</span>
					</div>
					<div className={`item-quantity not-mobile ${!product.replacement ? 'deleted-product' : ''}`}>
						<span>
							<span className="grey-label">{t.nrOfPurchases}:</span>
							<br />
							<span>{product.nrOfOrders}</span>
						</span>
					</div>
					<div className="last-purchased not-mobile">
						<span>
							<span className="grey-label">{t.lastPurchased}:</span>
							<br />
							<span>
								<Link to={`${urls.yourOrders}/${product.lastOrderId}`}>
									{dateTimeFormat(new Date(product.lastPurchase), true)} {product.lastCity}
								</Link>
							</span>
						</span>
					</div>

					{!deleted ? (
						<>
							<div className="price">
								<PriceTagSimple
									price={getFinalPrice(
										product.product.id,
										product.product.price,
										1,
										product.product.campaignPrice
									)}
									vatRate={product.product.vatRate}
									unit={product.unit}
								></PriceTagSimple>
							</div>
							<div className="purchase-buttons">
								<div className="amount">
									<QuantityInput
										defaultValue={quantityField}
										onBlur={(value) => setQuantityField(value)}
										step={product.product.bundleSize}
									/>
								</div>
								<div className="buy-button plausible-event-name=Add+to+Cart">{buyButton}</div>
							</div>
						</>
					) : product.replacement === false ? (
						<>
							<div className="price">
								<strong>{t.discontinued}</strong>
							</div>
							<div className="purchase-buttons">
								<div className="amount not-mobile" />
								<div className="buy-button replacement">
									<ButtonLink className="secondary" linkTo={`${urls.search}?q=${product.name}`}>
										{t.findReplacement}
									</ButtonLink>
								</div>
							</div>
						</>
					) : (
						<div className="purchase-buttons">
							<div className="buy-button">
								<strong>{t.productReplaced}</strong>
							</div>
						</div>
					)}
				</div>
			</div>

			{product.replacement !== false && (
				<div className="previously-bought-products-row replacement">
					<div className="image">
						<Icon shape={replacement_arrow} />
						<Link to={'/' + product.replacement.perma}>
							<Img
								ref={imgRef}
								src={product.replacement.image}
								alt={product.replacement.name}
								width={50}
								height={50}
							/>
						</Link>
					</div>
					<div className="previously-bought-products-details-wrapper">
						<div className="product">
							<Link to={'/' + product.replacement.perma}>{product.replacement.name}</Link>
						</div>
						<div className="item-no not-mobile">
							<span>
								<span className="grey-label">{t.itemNo}:</span>
								<br />
								<span>{product.replacement.displayId}</span>
							</span>
						</div>
						<div className="item-quantity not-mobile" />
						<div className="last-purchased not-mobile" />
						<div className="price">
							<PriceTagSimple
								price={getFinalPrice(
									product.replacement.id,
									product.replacement.price,
									1,
									product.replacement.campaignPrice
								)}
								vatRate={product.replacement.vatRate}
								unit={product.replacement.unit}
							></PriceTagSimple>
						</div>
						<div className="purchase-buttons">
							<div className="amount">
								<QuantityInput
									defaultValue={quantityField}
									onBlur={(value) => setQuantityField(value)}
									step={product.replacement.bundleSize}
								/>
							</div>
							<div className="buy-button plausible-event-name=Add+to+Cart">{buyButton}</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export const PreviouslyBoughtList = ({ products }: { products: ApiPreviouslyBoughtProduct[] }): JSX.Element => {
	return (
		<>
			{products.map((product) => (
				<PreviouslyBoughtItem key={product.id} product={product} />
			))}
		</>
	);
};
